// ** Custom Style
import "@styles/react/pages/page-authentication.scss";

import { Col, Row } from "reactstrap";

import Flex from "@components/ui-components/flex";
import { FormProvider } from "react-hook-form";
import GrantPermissionsModal from "./grant-permissions-modal";
import ImageWrapper from "@components/ui-components/image-wrapper";
import InputField from "@src/@core/components/fields/input-field";
import LoadingButton from "@components/ui-components/loading-button";
import PasswordField from "@src/@core/components/fields/password-field";
import { TabsView } from "@src/@core/components/views/tabs-view";
import TestimonialSlider from "./testimonial-slider";
import themeConfig from "@configs/theme-config";
import useLogin from "./hooks/use-login";
import { useTranslation } from "react-i18next";

const Login = () => {
    const {
        onSubmit,
        googleLoginLoading,
        emailLoginLoading,
        handleModalToggle,
        isOpen,
        methods,
        onEmailLogin,
        handleTabChange,
        activeTab
    } = useLogin();

    const {
        control,
        formState: { errors },
        setValue,
        watch
    } = methods;

    const {
        app: { logo }
    } = themeConfig;

    const { t } = useTranslation();

    return (
        <div className="auth-wrapper">
            <Row className="auth-inner m-0">
                <Col
                    className="d-none d-lg-flex flex-column px-5 text-left bg-bg-secondary"
                    lg="6"
                    sm="12">
                    <Flex column className="h-100 mt-5">
                        <div>
                            {/* Logo placeholder */}
                            <p className="brand-logo mb-5">
                                <ImageWrapper
                                    src={logo}
                                    alt="logo"
                                    width={200}
                                    id="brand-logo"/>
                            </p>
                            {/* Main heading */}
                            <p className="auth-heading mt-5 pt-5">
                                {t("Customer success, made simple.")}
                            </p>
                            <p className="auth-custom-desc mt-3">
                                {t(
                                    `From onboarding to retention, ${themeConfig.app.name} simplifies customer success - all from one place.`
                                )}
                            </p>
                        </div>
                        <TestimonialSlider />
                    </Flex>
                </Col>

                {/* Right Side - White Login Section */}
                <Col className="bg-white px-4 py-5 d-flex flex-column justify-content-center flex-column" lg="6" sm="12">
                    <div className="text-center">
                        <h1 className="auth-secondary-heading mb-1">
                            {t("Login Now")}
                        </h1>
                    </div>
                    <TabsView
                        active={activeTab}
                        headerClassName="mb-3"
                        centered
                        toggle={handleTabChange}
                        tabs={[
                            {
                                id     : "viaEmail",
                                name   : "Via Email",
                                content: (
                                    <FormProvider {...methods}>
                                        <h3 className="auth-custom-para d-flex justify-content-center mt-2">
                                            {t(
                                                "Enter your email below to login to your account"
                                            )}
                                        </h3>
                                        <InputField
                                            label="Email"
                                            name="email"
                                            id="email"
                                            type="email"
                                            placeholder="Enter your email"
                                            required
                                            control={control}
                                            errors={errors}/>

                                        <PasswordField
                                            label="Password"
                                            name="password"
                                            id="password"
                                            placeholder="Enter your password"
                                            required
                                            control={control}
                                            errors={errors}/>

                                        <LoadingButton
                                            testId="email-login"
                                            onClick={onEmailLogin}
                                            text="Login"
                                            loading={emailLoginLoading}
                                            className="w-100"/>
                                    </FormProvider>
                                )
                            },
                            {
                                id     : "viaGoogle",
                                name   : "Via Google",
                                content: (
                                    <FormProvider {...methods}>
                                        <Flex className="mt-2">
                                            <LoadingButton
                                                testId="login-google"
                                                onClick={() => {
                                                    setValue(
                                                        "type",
                                                        "normal"
                                                    );
                                                    onSubmit();
                                                }}
                                                text="Login with Google"
                                                loading={watch("type") === "normal" ? googleLoginLoading : false}
                                                className="w-100"/>

                                            <LoadingButton
                                                testId="login-admin"
                                                outline
                                                onClick={() => handleModalToggle("admin")
                                                }
                                                text="Login with Google As Admin"
                                                loading={watch("type") === "admin" ? googleLoginLoading : false}
                                                className="w-100"/>
                                        </Flex>

                                        <GrantPermissionsModal
                                            isOpen={isOpen}
                                            handleSubmit={onSubmit}
                                            handleModal={handleModalToggle}
                                            loading={googleLoginLoading}
                                            methods={methods}/>
                                    </FormProvider>
                                )
                            }
                        ]}/>

                    <div className="text-center mt-5">
                        <small>
                            © {new Date().getFullYear()}{" "}
                            {t("CS Next All Rights Reserved.")}
                        </small>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default Login;
